import React from "react"
import {Row} from "@irati/ui/src/Layout"
import {RouteComponentProps} from "react-router-dom"

import adminTracking from "@irati-tracking/admin"
import workosService from "@irati/workos/services/workos"
import Loading from "@irati/ui/src/Loading"
import authentication from "@irati/auth"
import mfaService from "@irati/auth/services/mfa"
import Flex from "@irati/ui/src/Flex"
import addOrRemoveFromArray from "@irati/utils/addOrRemoveFromArray"
import {User, Team, WorkOsOrganisation} from "@irati/types"
import YouNeedToMfa from "@irati-web/mfa/components/Error/YouNeedToMfa"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import {IconTypes} from "@irati/ui/src/Icon"
import businessService from "@irati/identity/services/business"

import DropdownNavigation from "@irati/ui/src/DropdownNavigation"
import BusinessAdminHeader from "../../../components/AdminHeader/BusinessAdminHeader"
import ManageUsersBottomBar from "../../../components/Users/ManageUsersBottomBar"
import UserList from "../../../components/Users/UserList/UserList"
import DownloadUsersExport from "../../../components/Users/DownloadUsersExport"

import {FILTER_OPTIONS} from "../../../constants/filter"
import UserListData from "../../../components/UserList"
import {ADD_PEOPLE_MULTIPLE, ADD_PEOPLE_SINGLE, BULK_DEACTIVATE_PEOPLE, UPDATE_PEOPLE_MULTIPLE} from "../../../routes/routeConfig"
import UnusedLicensesBanner from "../../../components/UnusedLicensesBanner/index"
import DsyncIntegrationBanner from "../../../components/DsyncIntegrationBanner"
import FilterChipToggle from "../../../components/FilterChipToggle"

interface Props extends RouteComponentProps<{}, {}, {businessUnitId: string; toggleDatesFilter?: boolean}> {}

interface State {
  selectedEmployees: User[]
  isAddToTeamPopUpOpen: boolean
  mfaLoad: Promise<any>
  businessUnits: Team[]
  dsyncEnabled: boolean
  hasBusinessRestrictions: boolean
  showLincensesBanner: boolean
  filterToggle: boolean
  filterVariables: any
}

export class ManageUsers extends React.PureComponent<Props, State> {
  userList = React.createRef<UserListData>()

  constructor(props: Props) {
    super(props)
    this.state = {
      selectedEmployees: [],
      isAddToTeamPopUpOpen: false,
      mfaLoad: mfaService.hasFlag(),
      businessUnits: [],
      dsyncEnabled: false,
      hasBusinessRestrictions: true,
      showLincensesBanner: false,
      filterToggle: props?.location?.state?.toggleDatesFilter || false,
      filterVariables: {}
    }
  }

  handleMFARetry = () => {
    this.setState({mfaLoad: mfaService.hasFlag()})
  }

  componentDidMount() {
    this.getCountries()
    this.getWorkOsOrganisation()
    this.getBusinessRestrictions()
  }

  getCountries = () => {
    return officesForTenant().then((businessUnits) => {
      this.setState({businessUnits})
    })
  }

  getWorkOsOrganisation = () => {
    const businessId = authentication.business().uuid
    if (businessId) {
      workosService
        .getOrganisation(businessId)
        .then((workOSOrganisation: WorkOsOrganisation) => {
          this.setState({dsyncEnabled: !!workOSOrganisation.directoryId})
          if (workOSOrganisation.directoryId && workOSOrganisation.directoryId !== "") {
            this.setState({showLincensesBanner: true})
          }
        })
        .catch(() => {
          this.setState({showLincensesBanner: true})
        })
    }
  }

  getBusinessRestrictions = () => {
    businessService
      .getBusinessRestrictions(authentication.business().uuid, {fetchPolicy: "network-only"})
      .then(({hasMaxUserLimit}) => {
        if (!hasMaxUserLimit) {
          this.setState({
            hasBusinessRestrictions: false
          })
        }
      })
      .catch(() => {
        this.setState({
          hasBusinessRestrictions: false
        })
      })
  }

  toggleSelectedUsers = (user: User) => {
    this.setState((state) => {
      const {selectedEmployees} = state

      const arr = addOrRemoveFromArray(selectedEmployees, user, (a, b) => a.uuid === b.uuid)

      return {
        selectedEmployees: arr
      }
    })
  }

  toggleAllUsers = (employees) => {
    this.setState((state) => {
      const {selectedEmployees} = state
      if (employees.length === selectedEmployees.length) {
        return {selectedEmployees: []}
      }
      return {selectedEmployees: [...employees]}
    })
  }

  handleAddPeopleSubmit = () => {
    if (this.userList.current) {
      this.userList.current.refetch()
    }
  }

  handleActivationStatusChange = () => {
    this.setState({selectedEmployees: []})
  }

  handleUpdateVariables = (filterVariables) => {
    this.setState({filterVariables})
  }

  render() {
    const {
      selectedEmployees,
      mfaLoad,
      businessUnits,
      dsyncEnabled,
      showLincensesBanner,
      filterToggle,
      filterVariables,
      hasBusinessRestrictions
    } = this.state
    const teamId = this.props.location?.state?.businessUnitId || undefined

    return (
      <Loading load={mfaLoad} Error={() => <YouNeedToMfa onRetry={this.handleMFARetry} />}>
        <Row>
          <>
            <DsyncIntegrationBanner show={!dsyncEnabled && !hasBusinessRestrictions} />
            {showLincensesBanner && <UnusedLicensesBanner />}
            <BusinessAdminHeader>
              <Flex alignItems="center" justifyContent="flex-end" wrap="wrap" gap="8px 16px">
                <DropdownNavigation
                  title="admin.manageUsers.button.addNewPeople"
                  options={[
                    {
                      id: "admin.manageUsers.button.addMultiplePeople",
                      onClick: adminTracking.navigationClickAddByCSV,
                      text: "admin.manageUsers.button.addMultiplePeopleCSV",
                      link: ADD_PEOPLE_MULTIPLE
                    },
                    {
                      id: "adminSection_people_addOnePerson_button",
                      onClick: adminTracking.navigationClickAddSinglePerson,
                      text: "admin.manageUsers.button.addOnePerson",
                      link: ADD_PEOPLE_SINGLE
                    }
                  ]}
                />
                <DropdownNavigation
                  title="admin.manageUsers.button.modifyPeople"
                  options={[
                    {
                      id: "adminSection_people_editByCSV_button",
                      text: "admin.manageUsers.button.editPeople",
                      link: UPDATE_PEOPLE_MULTIPLE
                    },
                    {
                      id: "adminSection_people_deactivateByCSV_button",
                      onClick: adminTracking.navigationClickDeactivateByCSV,
                      text: "admin.manageUsers.button.deletePeople",
                      link: BULK_DEACTIVATE_PEOPLE
                    }
                  ]}
                />
                <DownloadUsersExport
                  onDownload={() => adminTracking.donloadPeopleListFromPeoplePage()}
                  filterVariables={filterVariables}
                />
                <FilterChipToggle
                  id="adminSection_manageUsers_filters_button"
                  icon={IconTypes.filter3}
                  isToggled={filterToggle}
                  onClick={() => this.setState({filterToggle: !this.state.filterToggle})}
                  aria-label="toggle visibility of filters for managaing users in the admin section"
                />
              </Flex>
            </BusinessAdminHeader>
            <UserListData
              ref={this.userList}
              businessUnits={businessUnits}
              filterOptions={FILTER_OPTIONS}
              teamId={teamId}
              filterToggle={filterToggle}
              onUpdateVariables={this.handleUpdateVariables}>
              {({users, variables}) => (
                <>
                  <UserList
                    variables={variables}
                    employees={users}
                    businessUnits={businessUnits}
                    selectedEmployees={selectedEmployees}
                    onToggle={this.toggleSelectedUsers}
                    onToggleAll={() => this.toggleAllUsers(users)}
                    onUpdate={this.handleAddPeopleSubmit}
                  />
                  <ManageUsersBottomBar
                    variables={variables}
                    selectedEmployees={selectedEmployees}
                    onSave={this.handleActivationStatusChange}
                  />
                </>
              )}
            </UserListData>
          </>
        </Row>
      </Loading>
    )
  }
}

export default ManageUsers
